import {
  computed,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue'

import type { ReportColumn } from '../models/column.types'
import type {
  ReportTablePreferenceV1,
  ReportTableSortV1,
  ResolvedReportTableConfig,
} from '../models/tableConfig.types'
import { resolveReportTablePreference } from '../service/reportTableConfig'
import { useCustomizeStore } from '../stores/customizeStore'

import { useAccount, useWorkspace } from '@/features/account'

const PREFERENCE_KIND = 'tablePreferenceV1'

export interface UseReportTableViewStateOptions {
  id: MaybeRefOrGetter<string>
  partitionName?: MaybeRefOrGetter<string | undefined>
  columns: MaybeRefOrGetter<readonly ReportColumn[]>
  config: MaybeRefOrGetter<ResolvedReportTableConfig>
}

export interface UseReportTableViewState {
  preference: Ref<ReportTablePreferenceV1>
  sort: ShallowRef<ReportTableSortV1 | null>
  hasPreference: ComputedRef<boolean>
  hydrate: (preference: ReportTablePreferenceV1) => void
  setSort: (sort: ReportTableSortV1 | null) => void
  setColumnOrder: (fields: readonly string[]) => void
  setColumnWidths: (widths: Readonly<Record<string, number>>) => void
}

/**
 * Report-table interaction state. Product defaults and cached user choices stay separate so a
 * later schedule-tab config can replace defaults without turning those defaults into preferences.
 */
export function useReportTableViewState(
  options: UseReportTableViewStateOptions,
): UseReportTableViewState {
  const account = useAccount()
  const workspace = useWorkspace()
  const cache = useCustomizeStore()
  const rawPreference = ref<unknown>()
  const initialPreference = resolveReportTablePreference(
    undefined,
    toValue(options.columns),
    toValue(options.config),
  )
  const preference = ref<ReportTablePreferenceV1>(initialPreference)
  // Keep request-driving sort identity independent from presentation-only preference object churn.
  const sort = shallowRef<ReportTableSortV1 | null>(initialPreference.sort)
  const partition = computed(() => {
    const accountId = account.accountId.value
    const userId = account.userId.value
    const workspaceId = workspace.workspaceId.value
    if (userId === undefined || accountId === undefined || workspaceId === undefined) return null
    const reportPartition = toValue(options.partitionName) || 'default'
    return `report-table:${userId}:${accountId}:${String(workspaceId)}:${reportPartition}:${toValue(options.id)}`
  })

  function apply(next: ReportTablePreferenceV1): void {
    preference.value = next
    if (next.sort?.field !== sort.value?.field || next.sort?.order !== sort.value?.order)
      sort.value = next.sort
  }

  function reconcile(): void {
    apply(
      resolveReportTablePreference(
        rawPreference.value,
        toValue(options.columns),
        toValue(options.config),
      ),
    )
  }

  watch(
    partition,
    (nextPartition) => {
      rawPreference.value = nextPartition
        ? cache.getCached<unknown>(nextPartition, PREFERENCE_KIND)
        : undefined
      reconcile()
    },
    { immediate: true },
  )

  // Catalog and product config changes reconcile the same raw choice; defaults are never cached
  // until the user performs an interaction.
  watch([() => toValue(options.columns), () => toValue(options.config)], reconcile, { deep: true })

  function commit(value: ReportTablePreferenceV1): void {
    const next = resolveReportTablePreference(
      value,
      toValue(options.columns),
      toValue(options.config),
    )
    rawPreference.value = next
    apply(next)
    if (partition.value) cache.setCached(partition.value, PREFERENCE_KIND, next)
  }

  return {
    preference,
    sort,
    hasPreference: computed(
      () =>
        typeof rawPreference.value === 'object' &&
        rawPreference.value !== null &&
        (rawPreference.value as { version?: unknown }).version === 1,
    ),
    hydrate: commit,
    setSort: (sort) => commit({ ...preference.value, sort }),
    setColumnOrder: (fields) => {
      if (!toValue(options.config).allowColumnReorder) return
      commit({ ...preference.value, columnOrder: [...fields] })
    },
    setColumnWidths: (widths) => {
      if (!toValue(options.config).allowColumnResize) return
      commit({ ...preference.value, columnWidths: { ...widths } })
    },
  }
}
